import { useContext } from "react";
import { css, cx } from "@emotion/css";
import APIContext from "../../contexts/APIContext";
import { Typography, images, Colors } from "@analyticsMD/quil";
import { styles as commonStyles } from "./common-styles";

import type { FC, ReactNode } from "react";
import RoundingButton from "../rounding/rounding-button";
import { Box } from "@mui/material";
import Configuration from "../../types/Configuration";

const styles = {
  ...commonStyles,
  patientInfoSection: css`
    background: url("${images.PatientBackground}") ${Colors.qlavender_80};
    background-size: cover;
    color: white;
  `,
  patientInfoHeader: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  `,
  patientError: css`
    align-items: start;
    margin-top: 0.5rem;
  `,
  patientInfoBody: css`
    font-size: 0.6875rem;
    margin-bottom: 1rem;
  `,
  patientInfoColumn: css`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    margin-top: 0.25rem;
  `,
};

const sxStyles = {
  patientNameHeader: {
    fontSize: "1.625rem",
    fontWeight: "bold",
    minHeight: "2rem",
    marginBottom: "1rem",
    lineHeight: "1.2",
    wordWrap: "break-word",
    overflowWrap: "break-word",
  },
  patientInfoContent: {
    fontSize: "0.75rem",
    lineHeight: "1.125",
  },
};

interface PatientInfoSectionProps {
  showRoundingMode?: boolean;
  showDemographics?: boolean;
  showError?: boolean;
  useSidebarView?: boolean;
  closeButtonCmpt?: ReactNode;
}
const PatientInfoSection: FC<PatientInfoSectionProps> = ({
  showError,
  showRoundingMode,
  showDemographics = false,
  useSidebarView = false,
  closeButtonCmpt = null,
}) => {
  const { configurationQuery, patientQuery } = useContext(APIContext);

  const configuration: Configuration = configurationQuery?.data
    ? configurationQuery?.data?.[0]
    : {};
  let headerDisplay = "";
  if (patientQuery?.isSuccess && patientQuery.data) {
    headerDisplay = patientQuery.data.current_bed
      ? `${patientQuery.data.full_name} (${patientQuery.data.current_bed})`
      : patientQuery.data.full_name;
  }

  const demographics = () => {
    return (
      <div className={styles.patientInfoBody}>
        <div className={styles.patientInfoColumn}>
          <Typography sx={sxStyles.patientInfoContent}>
            DOB: {patientQuery.data.birth_date} ({patientQuery.data.age})
          </Typography>
          <Typography sx={sxStyles.patientInfoContent}>
            Admitting Dx: {patientQuery.data.admitting_diagnosis}
          </Typography>
          {configuration.payor_display_enabled && (
            <Typography sx={sxStyles.patientInfoContent}>
              Payor: {patientQuery.data.payor}
            </Typography>
          )}
          <Typography sx={sxStyles.patientInfoContent}>
            Att: {patientQuery.data.attending_provider}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <Box className={cx(styles.headerSection, styles.patientInfoSection)}>
      <Box
        className={
          !showError
            ? styles.patientInfoHeader
            : cx(styles.patientInfoHeader, styles.patientError)
        }
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={sxStyles.patientNameHeader}>
            {headerDisplay}
          </Typography>
          {closeButtonCmpt && (
            <Box sx={showError ? { marginTop: "-1rem" } : null}>
              {closeButtonCmpt}
            </Box>
          )}
        </Box>
        {!showError && showDemographics && demographics()}
        {!showError &&
          configuration &&
          configuration.rounding_mode_enabled &&
          showRoundingMode && (
            <Box>
              <RoundingButton hideRoundingButton={useSidebarView} />
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default PatientInfoSection;
